import {
  GbGrundstueckListEntryMeta,
  GrundbuchgeschaeftRestDetailEntryMeta,
  GrundstueckDetailEntryMeta,
  GrundstueckIcpListEntry,
  GrundstueckListEntry,
  PersonStammMasterListEntryMeta,
  RechtMasterListEntry,
  RechtVersionListEntry,
  StandardrechtMasterDetailEntryMeta
} from '../../../core/gb-api';
import { FormatRechteIdPipe } from '../../pipes/format/format-rechte-id.pipe';
import { TranslateService } from '@ngx-translate/core';
import { KompaktGrundstueckPipe } from '../../kompaktdarstellung/grundstueck/kompakt-grundstueck.pipe';
import { Injectable, OnDestroy } from '@angular/core';
import { GbPrintService } from '../../../core/services/app-print.service';
import { AuszugPrintOptions, AuszugSettingsService } from './settings/auszug-settings-service';
import { GrundstuecknummerPipe } from '../../pipes/simple/grundstuecknummer.pipe';
import { Subscription } from 'rxjs';
import { AppAuszugCookieService } from '../../../core/services/app-auszug-cookie-service';
import { ActivatedRoute } from '@angular/router';
import { KompaktGrundstueckDetailPipe } from '../../kompaktdarstellung/grundstueck/grundstueck-detail/kompakt-grundstueck-detail.pipe';
import { PersonStammMasterDetailEntryMeta, PersonStammVersionDetailEntryMeta } from '../../../core/personstamm-api';
import { FormatGeschaeftsnummerPipe } from '../../pipes/format/format-geschaeftsnummer.pipe';
import RechtArtEnum = RechtVersionListEntry.RechtArtEnum;
import PersonArtEnum = PersonStammVersionDetailEntryMeta.ArtEnum;
import GrundstueckArtEnum = GrundstueckListEntry.GrundstueckArtEnum;

export type AuszugModel =
  | StandardrechtMasterDetailEntryMeta
  | RechtMasterListEntry
  | GrundstueckListEntry
  | GbGrundstueckListEntryMeta
  | GrundstueckIcpListEntry
  | PersonStammMasterDetailEntryMeta
  | PersonStammMasterListEntryMeta
  | GrundbuchgeschaeftRestDetailEntryMeta;

export type FormatEnum = 'PDF' | 'RTF';
export type AuszugTypeEnum = 'Recht' | 'Grundstueck' | 'Eigentuemer' | 'Grundbuchmeldung';

export interface AuszugInfoItem {
  id: string;
  type: AuszugTypeEnum;
  art?:
    | RechtArtEnum
    | PersonArtEnum
    | PersonStammVersionDetailEntryMeta.ArtEnum
    | GrundstueckArtEnum
    | GrundbuchgeschaeftRestDetailEntryMeta.StatusEnum
    | null;
  filePrefix: string;
  tooltip: string;
  printPDFPossible: boolean;
  printRTFPossible: boolean;
  srTitle: string;
  format: FormatEnum;
  settingsTitle: string;
}

@Injectable()
export class AuszugPrintService implements OnDestroy {
  private printSubscription: Subscription;
  private versionId: String;

  constructor(
    private formatRechteId: FormatRechteIdPipe,
    private formatGeschaeftsnummerPipe: FormatGeschaeftsnummerPipe,
    private auszugSettingsService: AuszugSettingsService,
    private translate: TranslateService,
    private printCookieSettingService: AppAuszugCookieService,
    private formatGrundstueck: KompaktGrundstueckPipe,
    private formatGrundstueckDetail: KompaktGrundstueckDetailPipe,
    private grundstueckNummerPipe: GrundstuecknummerPipe,
    private appPrintService: GbPrintService,
    private route: ActivatedRoute
  ) {
    this.route.queryParamMap.subscribe((p) => (this.versionId = p.get('versionId')));
    this.printSubscription = this.auszugSettingsService.confirmedPrintSelection$.subscribe((printOptions) => {
      if (printOptions.hasToZip) {
        this.printAuszugZip(printOptions);
      } else {
        this.printAuszug(printOptions);
      }
    });
  }

  ngOnDestroy(): void {
    this.printSubscription?.unsubscribe();
  }

  zipGrundstueckAuszuege(selection: AuszugPrintOptions) {
    this.printCookieSettingService.saveGrundstueckCookies(selection.printOptions).then(() => {
      this.appPrintService.zipGrundstueckAuszuege(selection);
    });
  }

  printAuszug(selection: AuszugPrintOptions) {
    const printRtfFormat: boolean = selection.format === 'RTF';
    switch (selection.auszugType) {
      case 'Recht':
        this.printCookieSettingService.saveRechtCookies(selection.printOptions).then(() => {
          printRtfFormat
            ? this.appPrintService.getRechteAuszugRTF(selection.item, selection.indexNumber)
            : this.appPrintService.getRechteAuszugPDF(selection.item, selection.indexNumber);
        });
        break;
      case 'Grundstueck':
        this.printCookieSettingService.saveGrundstueckCookies(selection.printOptions).then(() => {
          printRtfFormat
            ? this.appPrintService.getGrundstueckAuszugRTF(selection.item, selection.indexNumber)
            : this.appPrintService.getGrundstueckAuszugPDF(selection.item, selection.indexNumber);
        });
        break;
      case 'Eigentuemer':
        this.printCookieSettingService.saveEigentuemerCookies(selection.printOptions).then(() => {
          printRtfFormat
            ? this.appPrintService.getEigentuemerAuszugRTF(selection.item)
            : this.appPrintService.getEigentuemerAuszugPDF(selection.item);
        });
        break;
      case 'Grundbuchmeldung':
        printRtfFormat
          ? this.appPrintService.getGrundbuchmeldungAuszugRTF(selection.item, selection.indexNumber)
          : this.appPrintService.getGrundbuchmeldungAuszugPDF(selection.item, selection.indexNumber);
        break;
      default:
        throw new Error(selection.auszugType + ' is not supported');
    }
  }

  public createAuszugFrom(model: AuszugModel, rechtVersionId?: string): AuszugInfoItem {
    if (!model) {
      return null;
    }
    if (this.isStandardrechtMasterDetailEntry(model)) {
      return this.createRechtAuszugFromDetail(model, rechtVersionId);
    }
    if (this.isRechtMasterListEntry(model)) {
      return this.createRechtAuszug(model);
    }
    if (this.isGrundstueckListEntry(model)) {
      return this.createGrundstueckAuszug(model);
    }
    if (this.isGrundstueckDetailEntry(model)) {
      return this.createGrundstueckDetailAuszug(model);
    }
    if (this.isPersonStammMasterDetailEntryMeta(model)) {
      return this.createEigentuemerAuszugFromDetail(model);
    }
    if (this.isPersonStammMasterListEntry(model)) {
      return this.createEigentuemerAuszug(model);
    }
    if (this.isGrundbuchgeschaeft(model)) {
      return this.createGrundbuchmeldungrAuszug(model);
    }
    return null;
  }

  private printAuszugZip(printOptions: any) {
    switch (printOptions.auszugType) {
      case 'Grundstueck':
        this.zipGrundstueckAuszuege(printOptions);
        break;
      case 'Recht':
        this.zipRechtAuszuege(printOptions);
        break;
      case 'Eigentuemer':
        this.zipEigentuemerAuszuege(printOptions);
        break;
    }
  }

  private zipRechtAuszuege(selection: AuszugPrintOptions) {
    this.printCookieSettingService.saveRechtCookies(selection.printOptions).then(() => {
      this.appPrintService.zipRechtAuszuege(selection);
    });
  }

  private zipEigentuemerAuszuege(selection: AuszugPrintOptions) {
    this.printCookieSettingService.saveEigentuemerCookies(selection.printOptions).then(() => {
      this.appPrintService.zipEigentuemerAuszuege(selection);
    });
  }

  private isGrundbuchgeschaeft(item: AuszugModel) {
    return item.hasOwnProperty('eingangTagebuch');
  }

  private isRechtMasterListEntry(item: AuszugModel) {
    return item.hasOwnProperty('id') && item.hasOwnProperty('rechteId') && item.hasOwnProperty('art') && item.hasOwnProperty('version');
  }

  private isGrundstueckListEntry(item: AuszugModel) {
    return item.hasOwnProperty('id') && item.hasOwnProperty('grundstueckNummer');
  }

  private isGrundstueckDetailEntry(item: AuszugModel) {
    return item.hasOwnProperty('id') && item.hasOwnProperty('grundstueckNummern');
  }

  private isStandardrechtMasterDetailEntry(item: AuszugModel) {
    return item.hasOwnProperty('lastRechtGruppen');
  }

  private isPersonStammMasterDetailEntryMeta(item: AuszugModel) {
    return item.hasOwnProperty('mEgbpid') && item.hasOwnProperty('masterId');
  }

  private isPersonStammMasterListEntry(item: AuszugModel) {
    return item.hasOwnProperty('adressen') && item.hasOwnProperty('masterId') && item.hasOwnProperty('version');
  }

  private createGrundstueckAuszug(item: AuszugModel): AuszugInfoItem {
    const grundstueck = <GrundstueckListEntry>item;
    const formatGrundstueck = this.formatGrundstueck.transform(grundstueck);
    return <AuszugInfoItem>{
      id: grundstueck.id.id,
      type: 'Grundstueck',
      filePrefix: formatGrundstueck,
      tooltip: this.getTooltip('Grundstueck', formatGrundstueck),
      printPDFPossible: grundstueck.grundstueckPDFpossible,
      printRTFPossible: grundstueck.grundstueckRTFpossible,
      srTitle: 'Grundstückauszug',
      art: grundstueck.grundstueckArt,
      settingsTitle:
        this.translate.instant(grundstueck.grundstueckArt) + ' ' + this.grundstueckNummerPipe.transform(grundstueck.grundstueckNummer)
    };
  }

  private createGrundstueckDetailAuszug(item: AuszugModel): AuszugInfoItem {
    const grundstueck = <GrundstueckDetailEntryMeta>item;
    const formatGrundstueck = this.formatGrundstueckDetail.transform(grundstueck);
    return <AuszugInfoItem>{
      id: grundstueck.id.id,
      type: 'Grundstueck',
      filePrefix: formatGrundstueck,
      tooltip: this.getTooltip('Grundstueck', formatGrundstueck),
      printPDFPossible: grundstueck.grundstueckPDFpossible,
      printRTFPossible: grundstueck.grundstueckRTFpossible,
      srTitle: 'Grundstückauszug',
      art: grundstueck.grundstueckArt,
      settingsTitle:
        this.translate.instant(grundstueck.grundstueckArt) +
        ' ' +
        this.grundstueckNummerPipe.transform(grundstueck.grundstueckNummern[grundstueck.grundstueckNummern.length - 1])
    };
  }

  private createEigentuemerAuszug(item: AuszugModel) {
    const person = <PersonStammMasterListEntryMeta>item;
    return <AuszugInfoItem>{
      id: person.masterId.id,
      type: 'Eigentuemer',
      art: person.version.art,
      filePrefix: [person.version.name, person.version.vorname].filter((val) => val).join('_'),
      tooltip: this.getTooltip('Eigentuemer', [person.version.name, person.version.vorname].filter((val) => val).join(' ')),
      printPDFPossible: person.printPDFpossible,
      printRTFPossible: person.printRTFpossible,
      srTitle: 'Eigentümerauszug',
      settingsTitle: [person.version.name, person.version.vorname].filter((val) => val).join(' ')
    };
  }

  private createEigentuemerAuszugFromDetail(item: AuszugModel) {
    const person = <PersonStammMasterDetailEntryMeta>item;
    return <AuszugInfoItem>{
      id: person.masterId.id,
      type: 'Eigentuemer',
      art: person.version.art,
      filePrefix: [person.version.name, person.version.vorname].filter((val) => val).join('_'),
      tooltip: this.getTooltip('Eigentuemer', [person.version.name, person.version.vorname].filter((val) => val).join(' ')),
      printPDFPossible: person.printPDFpossible,
      printRTFPossible: person.printRTFpossible,
      srTitle: 'Eigentümerauszug',
      settingsTitle: [person.version.name, person.version.vorname].filter((val) => val).join(' ')
    };
  }

  private createRechtAuszug(item: AuszugModel) {
    const recht = <RechtMasterListEntry>item;
    const version = recht.version;
    const rechteId = this.formatRechteId.transform(recht.rechteId);
    return <AuszugInfoItem>{
      id: version.id.id,
      type: 'Recht',
      art: recht.art,
      filePrefix: rechteId,
      tooltip: this.getTooltip('Recht', rechteId),
      printPDFPossible: version.printPDFpossible,
      printRTFPossible: version.printRTFpossible,
      srTitle: 'Rechteregisterauszug',
      settingsTitle: this.translate.instant(recht.art) + ' ' + rechteId
    };
  }

  private createRechtAuszugFromDetail(item: AuszugModel, rechtVersionId?: string): AuszugInfoItem {
    const recht = <StandardrechtMasterDetailEntryMeta>item;
    let version;
    version = recht.versions.filter((v) => v.id.id === rechtVersionId)[0];
    if (!version) {
      return null;
    }
    const rechteId = this.formatRechteId.transform(recht.rechteId);
    return <AuszugInfoItem>{
      id: version.id.id,
      type: 'Recht',
      art: recht.rechtArt,
      filePrefix: rechteId,
      tooltip: this.getTooltip('Recht', rechteId),
      printPDFPossible: version.printPDFpossible,
      printRTFPossible: version.printRTFpossible,
      srTitle: 'Rechteregisterauszug',
      settingsTitle: rechteId
    };
  }

  private createGrundbuchmeldungrAuszug(item: AuszugModel): AuszugInfoItem {
    const geschaeft = <GrundbuchgeschaeftRestDetailEntryMeta>item;
    const geschaeftsnummer = this.formatGeschaeftsnummerPipe.transform(geschaeft.timePoint.geschaeftsnummer);

    return <AuszugInfoItem>{
      id: geschaeft.id.id,
      type: 'Grundbuchmeldung',
      art: geschaeft.status,
      filePrefix: geschaeftsnummer,
      tooltip: this.getTooltip('Grundbuchmeldung', geschaeftsnummer),
      printPDFPossible: geschaeft.grundbuchmeldungPDFpossible,
      printRTFPossible: geschaeft.grundbuchmeldungRTFpossible,
      srTitle: 'Rechteregisterauszug',
      settingsTitle: geschaeftsnummer
    };
  }

  private getTooltip(type: string, param: string): string {
    return this.translate.instant('AuszugZu' + type + 'Erstellen', { param: param });
  }
}
